import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import styles from "./style.module.css";

// Ported from the Claude Design file "Delivery Updates Modal.dc.html".
// The design's "Let's Chat Now!" footer is intentionally left out.

const STATUS_LABELS = {
  "Awb Generated": "Order Placed",
  "Picked-up": "Shipped",
  Delivered: "Order Delivered",
  "In-transit": "In-Transit",
  "Out for Delivery": "Out for Delivery",
  "Pending with Shipper": "Pending with Shipper",
  "Return Initiated": "Reattempt Initiated",
};

const FAILED_STATUSES = [
  "RTO In-Transit",
  "RTO Out for Delivery",
  "RTO Delivered",
  "RTO Not Delivered",
  "RTO Delivery Delay",
  "Failed Delivered",
  "Failed Delivered Non-RTO",
  "Failed & Action Required",
  "Cancelled",
];

const label = (status) => STATUS_LABELS[status] || status;

// Tracking timestamps are IST wall-clock values stamped "+00:00", so read them
// as UTC to show the time exactly as recorded, whatever the browser's zone.
const ts = (value) => moment.utc(value);

const cx = (...c) => c.filter(Boolean).join(" ");

const CheckIcon = ({ size, width }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

const HeaderIcon = ({ kind }) => {
  if (kind === "delivered") return <CheckIcon size={20} width={2.4} />;
  if (kind === "failed")
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
        <path d="M12 7v6" />
        <path d="M12 17h.01" />
      </svg>
    );
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 7h11v9H3z" />
      <path d="M14 10h4l3 3v3h-7" />
      <circle cx="7" cy="17.5" r="1.8" />
      <circle cx="17" cy="17.5" r="1.8" />
    </svg>
  );
};

const groupByDay = (events) => {
  const groups = [];
  events.forEach((event) => {
    const key = event.timestamp ? ts(event.timestamp).format("YYYY-MM-DD") : "unknown";
    let group = groups.find((g) => g.key === key);
    if (!group) {
      group = { key, title: event.timestamp ? ts(event.timestamp).format("dddd, D MMM") : "", items: [] };
      groups.push(group);
    }
    group.items.push(event);
  });
  return groups;
};

const DeliveryUpdatesModal = ({ trackingData = [], steps = [], shipmentStatus, onClose }) => {
  const closeRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    // Move focus into the dialog for keyboard and screen-reader users without a visible ring.
    closeRef.current?.focus({ preventScroll: true, focusVisible: false });
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const latestStatus = trackingData[0]?.shipmentStatus || shipmentStatus;
  const failed = FAILED_STATUSES.includes(shipmentStatus);
  const kind = failed ? "failed" : latestStatus === "Delivered" ? "delivered" : "progress";
  const groups = groupByDay(trackingData);

  return createPortal(
    <div className={styles.wrap}>
      <div className={styles.overlay} onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label="All Delivery Updates" className={cx(styles.sheet, failed && styles.failed)}>
        <span className={styles.grab} />
        <div className={styles.head}>
          <h2 className={styles.heading}>All Delivery Updates</h2>
          <button ref={closeRef} type="button" aria-label="Close" className={styles.close} onClick={onClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.statusCard}>
            <div className={styles.statusRow}>
              <span className={styles.statusIcon}>
                <HeaderIcon kind={kind} />
              </span>
              <div className={styles.statusText}>
                <span className={styles.statusEyebrow}>Order status</span>
                <span className={styles.statusTitle}>{label(latestStatus)}</span>
              </div>
            </div>

            {steps.length > 0 && (
              <div className={styles.stepper} style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
                {steps.map((step, i) => {
                  const done = !!step.exist;
                  const nextDone = !!steps[i + 1]?.exist;
                  return (
                    <div className={styles.step} key={step.status + i}>
                      <div className={styles.stepTrack}>
                        <span className={cx(styles.stepDot, !done && styles.stepDotPending)}>
                          {done && <CheckIcon size={11} width={3} />}
                        </span>
                        {i < steps.length - 1 && <span className={cx(styles.stepLine, !nextDone && styles.stepLinePending)} />}
                      </div>
                      <div className={styles.stepText}>
                        <span className={cx(styles.stepLabel, !done && styles.stepLabelPending)}>{label(step.status)}</span>
                        {step.timestamp && (
                          <>
                            <span className={styles.stepMeta}>{ts(step.timestamp).format("DD MMM, YYYY")}</span>
                            <span className={styles.stepMeta}>{ts(step.timestamp).format("hh:mm A")}</span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {groups.map((group, g) => (
            <div className={styles.day} key={group.key}>
              {group.title && (
                <div className={styles.dayHead}>
                  <span className={styles.dayTitle}>{group.title}</span>
                  <span className={styles.dayRule} />
                </div>
              )}
              <div className={styles.events}>
                {group.items.map((event, i) => {
                  const latest = g === 0 && i === 0;
                  const last = i === group.items.length - 1;
                  return (
                    <div className={styles.event} key={event.trackingId || `${group.key}-${i}`}>
                      <div className={styles.rail}>
                        <span className={latest ? styles.dotLatest : styles.dot} />
                        {!last && <span className={styles.railLine} />}
                      </div>
                      <div className={styles.eventText}>
                        <span className={cx(styles.eventTitle, latest && styles.eventTitleLatest)}>{label(event.shipmentStatus)}</span>
                        {event.remarks && <span className={styles.eventDetail}>{event.remarks}</span>}
                      </div>
                      <span className={styles.eventTime}>{event.timestamp ? ts(event.timestamp).format("hh:mm A") : ""}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DeliveryUpdatesModal;
